import React, { useState, useEffect, useCallback } from "react";
import "./HrDashboard.css";
import { FaBell } from "react-icons/fa";
import { Link, useLocation } from 'react-router-dom';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
// import { ScrollView } from 'react-native';


const getBackendUrl = () => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:5000';
    }
    return `https://${hostname.replace('hr-frontend', 'hr-backend')}`;
  }
  return 'http://localhost:5000';
};

const Dashboard = () => {
  const [adminStats, setAdminStats] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [leaveAnalytics, setLeaveAnalytics] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [complianceMetrics, setComplianceMetrics] = useState(null);
  const [departmentAnalytics, setDepartmentAnalytics] = useState([]);
  const [leaveStatistics, setLeaveStatistics] = useState(null);
  const [dashboardTrends, setDashboardTrends] = useState(null);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userRole, setUserRole] = useState('Employee');
  const [user, setUser] = useState(null);

  // ✅ FIXED: Simplified employee counting state
  const [totalEmployeeCount, setTotalEmployeeCount] = useState(0);
  const [activeEmployeeCount, setActiveEmployeeCount] = useState(0);

  const [leaveLimits, setLeaveLimits] = useState({
    CASUAL: 0,
    MEDICAL: 0,
    PERMISSION: 0
  });

  const [policyStats, setPolicyStats] = useState({
    casual: { monthly_count: 0, avg_approval_time: 0 },
    medical: { monthly_count: 0, avg_approval_time: 0 },
    permission: { monthly_count: 0, avg_approval_time: 0 }
  });

  const API_BASE_URL = getBackendUrl();

  const getUserInitials = useCallback((name, email) => {
    try {
      if (name) {
        const parts = name.trim().split(/\s+/);
        if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
        return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
      }
      if (email) return email.charAt(0).toUpperCase();
    } catch (e) {
      // ignore
    }
    return 'U';
  }, []);

  const handleLogout = async () => {
    try {
      await fetch(`${API_BASE_URL}/auth/logout`, {
        method: 'GET',
        credentials: 'include'
      });
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
      window.location.href = '/';
    }
  };

  // ✅ FIXED: Simplified role checking function
  const isEmployeeRole = (role) => {
    if (!role) return false;
    const roleStr = String(role).toLowerCase().trim();
    return roleStr === 'employee';
  };

  // ✅ FIXED: Simplified status checking function
  const isActiveStatus = (employee) => {
    if (!employee) return false;
    
    // Check status field (can be string or boolean)
    const status = employee.status || employee.is_active;
    
    if (typeof status === 'string') {
      return status.toLowerCase().trim() === 'active';
    }
    if (typeof status === 'boolean') {
      return status === true;
    }
    
    // Default to active if no status field
    return true;
  };

  // ✅ FIXED: Check if employee is on full-day leave today
  const isOnFullDayLeaveToday = (employee) => {
    if (!employee || !employee.leaveRequests || !Array.isArray(employee.leaveRequests)) {
      return false;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (const req of employee.leaveRequests) {
      if (req.status?.toLowerCase() === 'approved') {
        try {
          const startDate = new Date(req.startDate || req.start_date || req.fromDate);
          const endDate = new Date(req.endDate || req.end_date || req.toDate || startDate);
          
          if (!isNaN(startDate) && !isNaN(endDate)) {
            startDate.setHours(0, 0, 0, 0);
            endDate.setHours(0, 0, 0, 0);
            
            if (today >= startDate && today <= endDate) {
              const leaveType = (req.leaveType || req.leave_type || req.type || '').toLowerCase();
              // Exclude permission/emergency (hour-based) leaves
              if (!leaveType.includes('permission') && !leaveType.includes('emergency')) {
                return true;
              }
            }
          }
        } catch (error) {
          console.warn('Error parsing leave dates:', error);
        }
      }
    }
    
    return false;
  };

  // ✅ FIXED: Simple and accurate employee counting
  const calculateEmployeeCounts = (employeeList) => {
    console.log('📊 ==========================================');
    console.log('📊 EMPLOYEE COUNT CALCULATION - FIXED');
    console.log('📊 ==========================================');
    console.log('📊 Total users in list:', employeeList?.length || 0);
    
    if (!Array.isArray(employeeList) || employeeList.length === 0) {
      console.log('⚠️ Invalid or empty employee list');
      return { total: 0, active: 0 };
    }

    // Step 1: Filter to only Employee role
    const employeesOnly = employeeList.filter(emp => {
      const role = emp.role || '';
      const isEmp = isEmployeeRole(role);
      console.log(`  👤 ${emp.name}: role="${role}" -> isEmployee=${isEmp}`);
      return isEmp;
    });

    console.log(`✅ Found ${employeesOnly.length} users with Employee role`);

    if (employeesOnly.length === 0) {
      return { total: 0, active: 0 };
    }

    // Step 2: Count active employees (active status AND not on full-day leave)
    let activeCount = 0;
    let breakdown = {
      activePresent: 0,
      activeOnPermission: 0,
      onFullDayLeave: 0,
      inactiveStatus: 0
    };
    
    for (const emp of employeesOnly) {
      const isActive = isActiveStatus(emp);
      const onFullDayLeave = isOnFullDayLeaveToday(emp);
      
      console.log(`  📋 ${emp.name}:`);
      console.log(`     - Status: ${isActive ? 'active' : 'inactive'}`);
      console.log(`     - On Full-Day Leave: ${onFullDayLeave}`);
      
      if (!isActive) {
        breakdown.inactiveStatus++;
        console.log(`     ❌ Not counted (inactive status)`);
        continue;
      }
      
      if (onFullDayLeave) {
        breakdown.onFullDayLeave++;
        console.log(`     ❌ Not counted (on full-day leave)`);
        continue;
      }
      
      // Active employee who is present (or only on permission)
      activeCount++;
      breakdown.activePresent++;
      console.log(`     ✅ COUNTED as ACTIVE`);
    }

    console.log('🎯 ==========================================');
    console.log('🎯 CALCULATION COMPLETE');
    console.log('🎯 ==========================================');
    console.log(`📊 Total Employees (role=Employee): ${employeesOnly.length}`);
    console.log(`✅ Active Employees: ${activeCount}`);
    console.log('📈 Breakdown:');
    console.log(`   - Active & Present: ${breakdown.activePresent}`);
    console.log(`   - On Full-Day Leave: ${breakdown.onFullDayLeave}`);
    console.log(`   - Inactive Status: ${breakdown.inactiveStatus}`);

    return {
      total: employeesOnly.length,
      active: activeCount,
      breakdown: breakdown
    };
  };

  const fetchAPI = async (endpoint, options = {}) => {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...options.headers
        },
        ...options
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API Error for ${endpoint}:`, error);
      throw error;
    }
  };

  const enrichEmployeeWithLeaveData = async (employee) => {
    try {
      const userLeaveRes = await fetchAPI(`/api/user-leave-requests?user_id=${employee.id}`);
      const userLeaveRequests = userLeaveRes && Array.isArray(userLeaveRes) ? userLeaveRes : [];
      
      return {
        ...employee,
        leaveRequests: userLeaveRequests
      };
    } catch (error) {
      console.warn(`Failed to fetch leave requests for employee ${employee.id}:`, error);
      return {
        ...employee,
        leaveRequests: []
      };
    }
  };

  // Load leave policies
  useEffect(() => {
    console.log('🔧 Dashboard: Loading leave policies configuration...');
    
    const loadConfiguration = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/leave-policies`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          }
        });

        if (response.ok) {
          const data = await response.json();
          console.log('📦 Dashboard: Received policy data:', data);
          
          if (data.leave_policies && Array.isArray(data.leave_policies)) {
            const newLimits = {
              CASUAL: 15,
              MEDICAL: 15,
              PERMISSION: 2
            };

            const newStats = {
              casual: { monthly_count: 0, avg_approval_time: 0 },
              medical: { monthly_count: 0, avg_approval_time: 0 },
              permission: { monthly_count: 0, avg_approval_time: 0 }
            };
            
            data.leave_policies.forEach(policy => {
              const leaveType = policy.leave_type?.toLowerCase() || '';
              
              if (leaveType.includes('casual')) {
                newLimits.CASUAL = policy.max_days_per_year || 15;
                if (policy.current_month_stats) {
                  newStats.casual = {
                    monthly_count: policy.current_month_stats.monthly_count || 0,
                    avg_approval_time: policy.current_month_stats.avg_approval_time_hours || 0
                  };
                }
              } else if (leaveType.includes('sick') || leaveType.includes('medical')) {
                newLimits.MEDICAL = policy.max_days_per_year || 15;
                if (policy.current_month_stats) {
                  newStats.medical = {
                    monthly_count: policy.current_month_stats.monthly_count || 0,
                    avg_approval_time: policy.current_month_stats.avg_approval_time_hours || 0
                  };
                }
              } else if (leaveType.includes('emergency') || leaveType.includes('permission')) {
                newLimits.PERMISSION = policy.max_days_per_year || 2;
                if (policy.current_month_stats) {
                  newStats.permission = {
                    monthly_count: policy.current_month_stats.monthly_count || 0,
                    avg_approval_time: policy.current_month_stats.avg_approval_time_hours || 0
                  };
                }
              }
            });
            
            console.log('✅ Dashboard: Updated leave limits:', newLimits);
            console.log('✅ Dashboard: Updated policy stats:', newStats);
            
            setLeaveLimits(newLimits);
            setPolicyStats(newStats);
          }
        }
      } catch (error) {
        console.error('❌ Dashboard: Error loading configuration:', error);
      }
    };

    loadConfiguration();

    const handleConfigUpdate = () => {
      console.log('🔄 Dashboard: Configuration update detected, reloading...');
      loadConfiguration();
    };

    window.addEventListener('configurationUpdated', handleConfigUpdate);
    window.addEventListener('storage', (e) => {
      if (e.key === 'leaveConfiguration') {
        handleConfigUpdate();
      }
    });

    return () => {
      window.removeEventListener('configurationUpdated', handleConfigUpdate);
    };
  }, [API_BASE_URL]);

  // ✅ FIXED: Main data fetching with proper employee counting
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('📊 Fetching dashboard data from APIs...');

        // Fetch user data
        const userData = await fetchAPI('/api/user').catch(() => null);
        setUser(userData);

        // Fetch employee list
        const employeeData = await fetchAPI('/api/employee-list').catch(err => {
          console.error('Error fetching employee list:', err);
          return [];
        });
        console.log('📋 Raw employee data:', employeeData);

        const transformedEmployees = (Array.isArray(employeeData) ? employeeData : 
          employeeData?.employees || employeeData?.data || []);

        console.log('👥 Transformed employees count:', transformedEmployees.length);
        
        // Enrich with leave data
        console.log('🔄 Enriching employees with leave request data...');
        const enrichedEmployees = await Promise.all(
          transformedEmployees.map(emp => enrichEmployeeWithLeaveData(emp))
        );
        
        console.log('✅ Employees enriched with leave data');
        setEmployees(enrichedEmployees);

        // ✅ FIXED: Calculate counts with the simplified function
        const counts = calculateEmployeeCounts(enrichedEmployees);
        setTotalEmployeeCount(counts.total);
        setActiveEmployeeCount(counts.active);
        
        console.log('🎯 FINAL EMPLOYEE COUNTS SET:', {
          total: counts.total,
          active: counts.active,
          breakdown: counts.breakdown
        });

        // Fetch user role
        const roleData = await fetchAPI('/api/user-role').catch(() => ({ role: 'Employee' }));
        const currentUserRole = roleData?.role || 'Employee';
        setUserRole(currentUserRole);

        console.log('🎯 Current User Role:', currentUserRole);

        const isManagerRole = ['Manager', 'HR', 'CEO', 'Superadmin'].includes(currentUserRole);
        console.log('🔐 Is Manager Role:', isManagerRole);

        // Fetch other dashboard data
        const dataPromises = [
          fetchAPI('/api/admin-stats').catch(err => null),
          fetchAPI('/api/leave-analytics').catch(err => null),
          fetchAPI('/api/manager/team-members').catch(err => ({ team_members: [] })),
          fetchAPI('/api/dashboard/compliance-metrics').catch(err => null),
          fetchAPI('/api/dashboard/recent-activity').catch(err => []),
          fetchAPI('/api/dashboard/department-analytics').catch(err => []),
          fetchAPI('/api/dashboard/leave-statistics').catch(err => null),
          fetchAPI('/api/dashboard/trends').catch(err => null)
        ];

        const [
          adminStatsData,
          analyticsData,
          teamData,
          complianceData,
          activityData,
          departmentData,
          leaveStatsData,
          trendsData
        ] = await Promise.all(dataPromises);

        setAdminStats(adminStatsData);
        setLeaveAnalytics(analyticsData);
        setTeamMembers(teamData?.team_members || []);

        // Get pending requests for managers
        let pendingRequestsList = [];
        
        if (isManagerRole) {
          console.log('📋 Fetching pending requests...');
          
          try {
            const response1 = await fetchAPI('/api/pending-approvals');
            console.log('Response from /api/pending-approvals:', response1);
            
            if (Array.isArray(response1)) {
              pendingRequestsList = response1.filter(req => {
                const isFromEmployee = !req.employeeRole || 
                                      req.employeeRole === 'Employee' ||
                                      req.employeeRole.toLowerCase() === 'employee';
                return isFromEmployee;
              });
              
              console.log(`✅ Got ${pendingRequestsList.length} employee requests`);
            }
          } catch (err1) {
            console.error('❌ Error fetching pending requests:', err1);
          }
        }
        
        setPendingRequests(pendingRequestsList);

        setComplianceMetrics(complianceData);
        setRecentActivity(activityData || []);
        setDepartmentAnalytics(departmentData || []);
        setLeaveStatistics(leaveStatsData);
        setDashboardTrends(trendsData);

        console.log('✅ All dashboard data fetched successfully');

      } catch (err) {
        console.error('❌ Error fetching dashboard data:', err);
        setError(`Failed to load dashboard data: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [API_BASE_URL]);

  // ✅ FIXED: Simplified stats calculation using the counts
  const calculateStats = useCallback(() => {
    const emptyStats = {
      totalEmployees: 0,
      activeUsers: 0,
      pendingRequests: 0,
      complianceScore: 0,
      utilizationRate: 0,
      avgApprovalTime: 0,
      avgApprovalTimeDisplay: '0h',
      monthlyLeaves: 0,
      employeeGrowth: 0,
      leaveGrowth: 0,
      utilizationGrowth: 0,
      approvalEfficiency: 0
    };

    if (!leaveLimits || leaveLimits.CASUAL === 0 || leaveLimits.MEDICAL === 0) {
      console.warn("⏳ Leave data not ready - using counts only");
      return {
        ...emptyStats,
        totalEmployees: totalEmployeeCount,
        activeUsers: activeEmployeeCount,
        pendingRequests: pendingRequests.length || 0
      };
    }

    // Use the already calculated counts
    const totalEmployees = totalEmployeeCount;
    const activeUsers = activeEmployeeCount;

    console.log('📊 Calculating stats with counts:', { totalEmployees, activeUsers });

    if (leaveStatistics) {
      const complianceScore = complianceMetrics?.compliance_score || 
                             leaveStatistics.complianceScore || 
                             emptyStats.complianceScore;

      let monthlyLeaves = 0;
      if (leaveStatistics.leaveRequests && Array.isArray(leaveStatistics.leaveRequests)) {
        monthlyLeaves = leaveStatistics.leaveRequests.filter(request => {
          const isEmployee = isEmployeeRole(request.employeeRole || request.role);
          const leaveDate = new Date(request.startDate || request.start_date);
          const currentDate = new Date();
          const isCurrentMonth = leaveDate.getMonth() === currentDate.getMonth() &&
                               leaveDate.getFullYear() === currentDate.getFullYear();
          
          return isEmployee && isCurrentMonth;
        }).length;
      } else {
        monthlyLeaves = leaveStatistics.employeeMonthlyLeaves || leaveStatistics.monthlyLeaves || 0;
      }

      let utilizationRate = 0;
      
      const CASUAL_LIMIT = leaveLimits.CASUAL || 15;
      const MEDICAL_LIMIT = leaveLimits.MEDICAL || 15;
      
      const MONTHLY_CASUAL_ENTITLEMENT = Math.round(CASUAL_LIMIT / 12); 
      const MONTHLY_SICK_ENTITLEMENT = Math.round(MEDICAL_LIMIT / 12);
      
      const totalMonthlyEntitlement = totalEmployees * (MONTHLY_CASUAL_ENTITLEMENT + MONTHLY_SICK_ENTITLEMENT);
      
      const casualUsed = policyStats?.casual?.monthly_count || 0;
      const medicalUsed = policyStats?.medical?.monthly_count || 0;
      const totalLeaveAvailed = casualUsed + medicalUsed;
      
      if (totalMonthlyEntitlement > 0 && totalLeaveAvailed >= 0) {
        utilizationRate = Math.min(100, Math.round((totalLeaveAvailed / totalMonthlyEntitlement) * 100));
      }

      let avgApprovalTime = 0;
      let avgApprovalTimeDisplay = '0h';
      
      if (leaveStatistics.avgApprovalTime && 
          typeof leaveStatistics.avgApprovalTime === 'number' && 
          leaveStatistics.avgApprovalTime > 0) {
        
        avgApprovalTime = Math.round(leaveStatistics.avgApprovalTime);
        
        if (avgApprovalTime < 24) {
          avgApprovalTimeDisplay = `${avgApprovalTime}h`;
        } else {
          const days = Math.floor(avgApprovalTime / 24);
          const hours = avgApprovalTime % 24;
          avgApprovalTimeDisplay = hours > 0 ? `${days}d ${hours}h` : `${days}d`;
        }
      }

      return {
        totalEmployees: totalEmployees,
        activeUsers: activeUsers,
        pendingRequests: leaveStatistics.pendingRequests || pendingRequests.length || 0,
        complianceScore: Math.round(complianceScore),
        utilizationRate: utilizationRate,
        avgApprovalTime: avgApprovalTime,
        avgApprovalTimeDisplay: avgApprovalTimeDisplay,
        monthlyLeaves: monthlyLeaves,
        employeeGrowth: dashboardTrends?.employeeGrowth || leaveStatistics.employeeGrowth || emptyStats.employeeGrowth,
        leaveGrowth: leaveStatistics.monthlyChange || dashboardTrends?.leaveGrowth || emptyStats.leaveGrowth,
        utilizationGrowth: dashboardTrends?.utilizationGrowth || emptyStats.utilizationGrowth,
        approvalEfficiency: leaveStatistics.approvalEfficiency || emptyStats.approvalEfficiency
      };
    }

    return {
      ...emptyStats,
      totalEmployees: totalEmployees,
      activeUsers: activeUsers,
      pendingRequests: pendingRequests.length || 0
    };
  }, [
    leaveLimits,
    policyStats,
    leaveStatistics,
    complianceMetrics,
    dashboardTrends,
    pendingRequests,
    totalEmployeeCount,
    activeEmployeeCount
  ]);

  const exportLeaveData = async () => {
    try {
      const stats = calculateStats();
      
      const casualUtilization = leaveLimits.CASUAL > 0 
        ? Math.round((policyStats.casual.monthly_count / leaveLimits.CASUAL) * 100)
        : 0;
      
      const medicalUtilization = leaveLimits.MEDICAL > 0
        ? Math.round((policyStats.medical.monthly_count / leaveLimits.MEDICAL) * 100)
        : 0;
      
      const permissionUtilization = leaveLimits.PERMISSION > 0
        ? Math.round((policyStats.permission.monthly_count / leaveLimits.PERMISSION) * 100)
        : 0;

      const csvContent = `Leave Type,Utilization,Approval Time,Monthly Count,Limit
Casual Leave,${casualUtilization}%,${policyStats.casual.avg_approval_time || stats.avgApprovalTime}h,${policyStats.casual.monthly_count},${leaveLimits.CASUAL} days
Medical Leave,${medicalUtilization}%,${policyStats.medical.avg_approval_time || stats.avgApprovalTime}h,${policyStats.medical.monthly_count},${leaveLimits.MEDICAL} days
Permission,${permissionUtilization}%,${policyStats.permission.avg_approval_time || Math.floor(stats.avgApprovalTime * 0.5)}h,${policyStats.permission.monthly_count},${leaveLimits.PERMISSION} hours`;

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'leave_summary.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting leave data:', error);
    }
  };

  const exportComplianceData = () => {
    const complianceData = getComplianceData().map(item => ({
      metric: item.label,
      percentage: item.percentage
    }));

    const csvContent = `Metric,Percentage
${complianceData.map(item => `${item.metric},${item.percentage}%`).join('\n')}`;

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'compliance_metrics.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const exportActivityData = () => {
    const csvContent = `Activity Type,User,Time,Details
${recentActivity.map(activity => 
      `${activity.type},"${activity.user}",${activity.time},"${activity.details || ''}"`
    ).join('\n')}`;

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'recent_activity.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const exportDepartmentData = () => {
    const departmentData = getDepartmentData();
    const csvContent = `Department,Active/Total,Percentage,On Leave Today
${departmentData.map(dept => 
      `${dept.name},${dept.count},${dept.percentage.toFixed(1)}%,${dept.on_leave_today || 0}`
    ).join('\n')}`;

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'department_overview.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const stats = calculateStats();

  const getComplianceData = () => {
    if (complianceMetrics && typeof complianceMetrics === 'object') {
      return [
        { 
          label: 'Leave Records Accuracy', 
          percentage: Math.round(complianceMetrics.leave_records_accuracy || 0), 
          color: 'complianceprogress-purple' 
        },
        { 
          label: 'Approval Timeline Compliance', 
          percentage: Math.round(complianceMetrics.approval_timeline_compliance || 0), 
          color: 'complianceprogress-gray' 
        },
        { 
          label: 'Documentation Completeness', 
          percentage: Math.round(complianceMetrics.documentation_completeness || 0), 
          color: 'complianceprogress-violet' 
        },
        { 
          label: 'Policy Adherence', 
          percentage: Math.round(complianceMetrics.policy_adherence || 0), 
          color: 'complianceprogress-cyan' 
        },
        { 
          label: 'Audit Trail Integrity', 
          percentage: Math.round(complianceMetrics.audit_trail_integrity || 0), 
          color: 'complianceprogress-lightgray' 
        }
      ];
    }

    return [
      { label: 'Leave Records Accuracy', percentage: 0, color: 'complianceprogress-purple' },
      { label: 'Approval Timeline Compliance', percentage: 0, color: 'complianceprogress-gray' },
      { label: 'Documentation Completeness', percentage: 0, color: 'complianceprogress-violet' },
      { label: 'Policy Adherence', percentage: 0, color: 'complianceprogress-cyan' },
      { label: 'Audit Trail Integrity', percentage: 0, color: 'complianceprogress-lightgray' }
    ];
  };

  const getDepartmentData = () => {
    if (departmentAnalytics && departmentAnalytics.length > 0) {
      return departmentAnalytics;
    }

    if (employees.length > 0) {
      const departments = {};
      employees.forEach(emp => {
        const dept = emp.department || emp.role || 'General';
        if (!departments[dept]) {
          departments[dept] = { total: 0, active: 0, on_leave: 0 };
        }
        departments[dept].total += 1;
        
        if (emp.on_leave_today) {
          departments[dept].on_leave += 1;
        } else {
          departments[dept].active += 1;
        }
      });

      const colors = ['pink', 'blue', 'cyan', 'purple', 'green'];
      return Object.entries(departments).slice(0, 5).map(([name, data], index) => ({
        name,
        count: `${data.active}/${data.total}`,
        percentage: data.total > 0 ? ((data.active / data.total) * 100) : 0,
        color: colors[index % colors.length],
        on_leave_today: data.on_leave
      }));
    }

    return [];
  };

  const complianceData = getComplianceData();
  const departmentData = getDepartmentData();

  // Circle visualization data
  const size = 200;
  const stroke = 10;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;

  const maxMonthlyLeaves = 10;
  const maxApprovalHours = 168;
  
  const arcs = [
    { 
      value: stats.utilizationRate / 100,
      color: "#ff4de3",
      label: "Leave Utilization"
    },
    { 
      value: stats.monthlyLeaves / maxMonthlyLeaves,
      color: "#2ecbff",
      label: `This Month Leaves (${stats.monthlyLeaves})`
    },
    { 
      value: stats.avgApprovalTime > 0 
        ? Math.min(1, stats.avgApprovalTime / maxApprovalHours)
        : 0,
      color: "#004eff",
      label: stats.avgApprovalTime > 0 ? `Approval Time (${stats.avgApprovalTimeDisplay})` : "Approval Time (No data)"
    },
  ];

  if (loading) {
    return (
      <div className="hr-dashboard-container">
        <div style={{ 
          padding: '20px', 
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '300px'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #3498db',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginBottom: '20px'
          }}></div>
          <p style={{ fontSize: '16px', color: '#666' }}>Loading dashboard data...</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div className="hr-dashboard-container">
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <div style={{
            backgroundColor: '#f8d7da',
            color: '#721c24',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #f5c6cb',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            <h3 style={{ margin: '0 0 10px 0' }}>Dashboard Error</h3>
            <p style={{ margin: '0 0 20px 0' }}>{error}</p>
            <button 
              onClick={() => window.location.reload()} 
              style={{ 
                padding: '10px 20px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Retry Loading
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="hr-dashboard-container">
      {/* <header className="header-container">
        <div className="header-left">
          <div className="logo-container">
            <img src="/Images/Manurevalogo.png" alt="MG" className="logo-image" />
            <span className="company-name">Manureva Digital Solutions</span>
          </div>
        </div>
        
        <div className="header-right">
          <div className="notification-icon">
            <FaBell />
          </div>
          <div className="user-info">
            <span className="user-name">{user?.name || user?.full_name || 'User'}</span>
            <span className="user-role">{userRole}</span>
          </div>
          <div className="user-avatar">
            {user?.profile_picture || user?.picture ? (
              <img src={user.profile_picture || user.picture} alt={user?.name || 'User'} style={{ width: '36px', height: '36px', borderRadius: '50%' }} />
            ) : (
              <span>{getUserInitials(user?.name || user?.full_name, user?.email)}</span>
            )}
          </div>
          <div className="power-icon" onClick={handleLogout} style={{ cursor: 'pointer' }} title="Logout">
            <PowerSettingsNewIcon/>
          </div>
        </div>
      </header>

      <div className="hr-sidebar">
        <Link to="/HrDashboard" className={location.pathname === '/Dashboard' ? 'active' : ''}>
          Dashboard
        </Link>
        <Link to="/hrempdash" className={location.pathname === '/hrempdash' ? 'active' : ''}>
          Employees
        </Link>
        <Link to="/report" className={location.pathname === '/report' ? 'active' : ''}>
          Report
        </Link>
        <Link to="/configuration" className={location.pathname === '/configuration' ? 'active' : ''}>
          Configuration
        </Link>
        <Link to="/Hrtimesheet" className={location.pathname === '/hrempdash' ? 'active' : ''}>
                  Hrtimesheet
                </Link>
      </div> */}
      <div className="hr-main">
        <div className="hr-head">
          <h2 className="hr-section-title" style={{marginLeft: window.innerWidth <= 768 ? '8px' : '29px'}} >Dashboard</h2>
        </div>

        <div className="Hr-stats-grid">
          <div className="hr-stat-card">
            <div className="stat-card-header" style={{display:'flex', marginTop:'10px', justifyContent:'space-between', alignItems:'center', width:'100%'}} >
              <h4 style={{color:'#2196F3'}}>Total Employee</h4>
              <img className="Hr-dashboard-img" src="/Images/Totalemp.png" alt="E" />
            </div>
            <p className="stat-value" style={{color:'black'}}>{stats.totalEmployees}</p>
          </div>
          <div className="hr-stat-card">
            <div className="stat-card-header" style={{display:'flex',marginTop:'10px', justifyContent:'space-between'}} >
              <h4 style={{color:'#009688'}}>Active Employees</h4>
              <img className="Hr-dashboard-img" src="/Images/Activeemp.png" alt="E" />
            </div>
            <p className="stat-value" style={{color:'black'}}>{stats.activeUsers}</p>
          </div>
          <div className="hr-stat-card warning">
            <div className="stat-card-header" style={{display:'flex',marginTop:'10px', justifyContent:'space-between'}} >
              <h4 style={{color:'#ff0004'}}>Pending Requests</h4>
              <img className="Hr-dashboard-img" src="/Images/Pendingrequest.png" alt="E" />
            </div>
            <p className="stat-value" style={{color:'black'}}>{stats.pendingRequests}</p>
          </div>
          <div className="hr-stat-card danger">
            <div className="stat-card-header" style={{display:'flex',marginTop:'10px', justifyContent:'space-between'}} >
              <h4 style={{color:'#FE7239'}}>Compliance Score</h4>
              <img className="Hr-dashboard-img" src="/Images/compilancescore.png" alt="E" />
            </div>
            <p className="stat-value" style={{color:'black'}}>{stats.complianceScore}%</p>
          </div>
        </div>

        <div className="widget-grid">
          <div className="leave-widget">
            <div className="leave-header">
              <h4>Leave Analytics</h4>
              <button className="leave-export-btn" onClick={exportLeaveData} 
                      disabled={!leaveStatistics || !stats.monthlyLeaves}>Export ↓</button>
            </div>

            <div className="leave-circle-container" style={{width: window.innerWidth <= 480 ? '200px' : '200px', height: window.innerWidth <= 480 ? '200px' : '200px'}}>
              {loading ? (
                <div className="loading-spinner">Loading...</div>
              ) : stats.monthlyLeaves === 0 ? (
                <div className="no-data-message">No leave data available</div>
              ) : (
                <>
                  <svg width={window.innerWidth <= 480 ? 200 : size} height={window.innerWidth <= 480 ? 200 : size}>
                    <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
                      {arcs.map((arc, i) => (
                        <circle
                          key={i}
                          cx={size / 2}
                          cy={size / 2}
                          r={radius - i * 15}
                          stroke={arc.color}
                          strokeWidth={stroke}
                          fill="none"
                          strokeDasharray={circumference}
                          strokeDashoffset={circumference * (1 - arc.value)}
                          strokeLinecap="round"
                          title={arc.label}
                        />
                      ))}
                    </g>
                  </svg>
                  <div className="leave-circle-center">{stats.monthlyLeaves}</div>
                </>
              )}
            </div>

            <div className="leave-stats">
              {/* <div className="stat" style={{flexWrap: window.innerWidth <= 768 ? 'wrap' : 'nowrap'}}> */}
              <div className="stat">  
                <span className="dot pink"></span>
                <div style={{marginLeft: window.innerWidth <= 768 ? '0' : '-1.5cm', flex: 1}} >
                  <p className="stat-title1" style={{textAlign:'center'}} >Leave Utilization</p>
                </div>
                <p className="leave-stat-value">{stats.utilizationRate}%</p>
              </div>

              <div className="stat">
                <span className="dot cyan"></span>
                <div style={{marginLeft:'-1.7cm'}} >
                  <p className="stat-title1">This Month Leaves</p>
                </div>
                <p className="leave-stat-value">{stats.monthlyLeaves || 0}</p>
              </div>

              <div className="stat">
                <span className="dot blue"></span>
                <div style={{marginLeft:'-1.5cm'}}  >
                  <p className="stat-title1">Avg Approval Time</p>
                </div>
                <p className="leave-stat-value">{stats.avgApprovalTimeDisplay}</p>
              </div>
            </div>
          </div>

          <div className="hr-compliance-container" style={{gap:'10px'}} >
            <div className="hr-compliance-header" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%'}}>
              <h2 className="hr-compliancetitle">Compliance Metrics</h2>
              {/* <button className="hr-complianceexport-btn" onClick={exportComplianceData} style={{marginLeft: window.innerWidth <= 768 ? 'auto' : '5.5cm', marginTop: window.innerWidth <= 768 ? '0' : '-20px'}}>Export ↓</button> */}
              <button className="hr-complianceexport-btn" onClick={exportComplianceData}>Export ↓</button>
            </div>
            
            <div className="hr-compliancemetrics-list">
              {complianceData.map((metric, index) => (
                <div key={index} className="hr-compliancemetric-item">
                  <div className="hr-compliancemetric-label">{metric.label}</div>
                  <div className="hr-complianceprogress-container">
                    <div className="hr-complianceprogress-bar" style={{width:'20px'}} >
                      <div 
                        className={`hr-complianceprogress-fill ${metric.color}`} 
                        style={{width: `${metric.percentage}%`}}
                      ></div>
                    </div>
                    <span className="hr-compliancepercentage">{metric.percentage}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="hr-activity-container">
            <div className="hr-activityheader" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', flexWrap: window.innerWidth <= 768 ? 'wrap' : 'nowrap'}}>
              <h2 className="hr-activitytitle">Recent HR Activity</h2>
              <button className="hr-activityexport-btn" onClick={exportActivityData}>Export ↓</button>
            </div>
            
            <div className="hr-activity-scroll" >
               {/* <ScrollView style={styles.scrollView}> */}
              {recentActivity.length > 0 ? (
                recentActivity.map((activity, index) => {
                  // Format timestamp to YYYY-MM-DD HH:MM
                  const formatTimestamp = (timestamp) => {
                    try {
                      const date = new Date(timestamp);
                      const year = date.getFullYear();
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const day = String(date.getDate()).padStart(2, '0');
                      const hours = String(date.getHours()).padStart(2, '0');
                      const minutes = String(date.getMinutes()).padStart(2, '0');
                      return `${year}-${month}-${day}  ${hours}:${minutes}`;
                    } catch (e) {
                      return timestamp;
                    }
                  };
                  
                  return (
                    <div key={index} className={`hr-activity-item ${index % 2 === 1 ? 'light' : ''}`}>
                      <div className="hr-activity-type">{activity.type}</div>
                      <div className="hr-activity-details">
                        <span className="hr-user-icon"> <img src="/Images/Recenthr.png" alt="👤" /> </span>
                        <span className="hr-activity-text">
                          {activity.user} • {formatTimestamp(activity.time)}
                        </span>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="no-data-message">No recent activity</div>
              )}
              {/* </ScrollView> */}
            </div>
            </div>
        </div>


        <div className="hr-department-overview-container-main">

          <div className="hr-department-overview-container">
            <div className="hr-department-header" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', flexWrap: window.innerWidth <= 768 ? 'wrap' : 'nowrap'}}>
              <div className="hr-department-title">Department Overview</div>
              <button className="hr-department-export-btn" onClick={exportDepartmentData}>Export ↓</button>
            </div>
            
            <div className="hr-department-list">
              {departmentData.map((dept, index) => (
                <div key={index} className="hr-department-item">
                  <div className="hr-department-info">
                    <div className={`hr-department-indicator ${dept.color}`}></div>
                    <span className="hr-department-name">{dept.name}</span>
                  </div>
                  <div className="hr-department-stats">
                    <span className="hr-fraction">{dept.count}</span>
                    <div className="hr-progress-bar">
                      <div 
                        className={`hr-progress-fill ${dept.color}-fill`} 
                        style={{width: `${dept.percentage}%`}}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>  
      </div>
    </div>
  );
};

export default Dashboard;